import { useState, useEffect, useCallback } from 'react';
import { AlertField } from '../config/alertFields';
import type { FlowOpportunity } from '../services/smartFlowService';
import {
  evaluateFlowAlertRule,
  formatFlowLiquidityNotification,
  type FlowAlertRule,
} from '../utils/flowLiquidityAlertUtils';
import {
  FLOW_ALERT_SETTINGS_CHANGED,
  loadFlowAlertSettings,
  saveFlowAlertSettings,
  type FlowAlertSettings,
} from '../utils/flowAlertSettingsStore';

type AlertRule = FlowAlertRule;
type AlertSettings = FlowAlertSettings;

interface ActiveAlert {
  id: string;
  message: string;
  timestamp: Date;
  rule: AlertRule;
  data: any;
}

export const useAlertSystem = () => {
  const [alertSettings, setAlertSettings] = useState<AlertSettings>(
    () => loadFlowAlertSettings(),
  );
  const [isAlertSoundEnabled, setIsAlertSoundEnabled] = useState(false);
  const [isAlertNotificationEnabled, setIsAlertNotificationEnabled] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState<ActiveAlert[]>([]);
  const [lastAlertTime, setLastAlertTime] = useState<Date | null>(null);

  const syncFromStore = useCallback(() => {
    const saved = loadFlowAlertSettings();
    setAlertSettings(saved);
    setIsAlertSoundEnabled(saved.soundEnabled);
    setIsAlertNotificationEnabled(saved.notificationEnabled);
  }, []);

  useEffect(() => {
    syncFromStore();
    const onExternal = () => syncFromStore();
    window.addEventListener(FLOW_ALERT_SETTINGS_CHANGED, onExternal);
    return () => window.removeEventListener(FLOW_ALERT_SETTINGS_CHANGED, onExternal);
  }, [syncFromStore]);

  const saveAlertSettings = useCallback((settings: AlertSettings) => {
    setAlertSettings(settings);
    setIsAlertSoundEnabled(settings.soundEnabled);
    setIsAlertNotificationEnabled(settings.notificationEnabled);
    saveFlowAlertSettings(settings);
  }, []);

  // Play alert sound
  const playAlertSound = useCallback(() => {
    if (!isAlertSoundEnabled) return;

    try {
      const audio = new Audio(`/${alertSettings.soundFile}`);
      audio.volume = alertSettings.volume / 100;
      audio.play().catch(error => {
        console.error('Error playing alert sound:', error);
      });
    } catch (error) {
      console.error('Error creating audio element:', error);
    }
  }, [isAlertSoundEnabled, alertSettings.soundFile, alertSettings.volume]);

  // Send browser notification
  const sendAlertNotification = useCallback(
    (message: string, title = 'Flow Alert') => {
      if (!isAlertNotificationEnabled) return;

      if ('Notification' in window) {
        const fire = () => {
          try {
            new Notification(title, { body: message, icon: '/logo.png' });
          } catch {
            /* ignore */
          }
        };
        if (Notification.permission === 'granted') {
          fire();
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') fire();
          });
        }
      }
    },
    [isAlertNotificationEnabled],
  );

  // Check if enough time has passed since last alert
  const canTriggerAlert = useCallback(() => {
    if (!lastAlertTime) return true;
    
    const now = new Date();
    const timeDiff = now.getTime() - lastAlertTime.getTime();
    const cooldownMs = alertSettings.cooldownMinutes * 60 * 1000;
    
    return timeDiff >= cooldownMs;
  }, [lastAlertTime, alertSettings.cooldownMinutes]);

  // Evaluate a single rule against flow row data
  const evaluateRule = useCallback((rule: AlertRule, data: FlowOpportunity): boolean => {
    return evaluateFlowAlertRule(
      data,
      rule as FlowAlertRule,
      alertSettings.custom_fields,
    );
  }, [alertSettings.custom_fields]);

  // Check and trigger alerts for data
  const checkAndTriggerAlerts = useCallback((data: any[]) => {
    if (!canTriggerAlert()) return [];

    const triggeredAlerts: ActiveAlert[] = [];
    const triggeredData: any[] = [];

    data.forEach((item, index) => {
      alertSettings.rules.forEach(rule => {
        if (evaluateRule(rule, item)) {
          // Check if we should only alert for new opportunities
          if (alertSettings.newOpportunitiesOnly && !item.isNew) {
            return; // Skip if new opportunities only is enabled and this is not new
          }

          const { title, body } = formatFlowLiquidityNotification(item);
          const alert: ActiveAlert = {
            id: `${rule.id}-${index}-${Date.now()}`,
            message: `${title} — ${body}`,
            timestamp: new Date(),
            rule,
            data: item
          };
          triggeredAlerts.push(alert);
          triggeredData.push(item);
        }
      });
    });

    if (triggeredAlerts.length > 0) {
      setActiveAlerts(prev => [...prev, ...triggeredAlerts]);
      setLastAlertTime(new Date());

      const top = triggeredAlerts[0];
      const topItem = top.data as FlowOpportunity;
      const { title, body } = formatFlowLiquidityNotification(topItem);

      playAlertSound();
      sendAlertNotification(
        triggeredAlerts.length > 1
          ? `${body} (+${triggeredAlerts.length - 1} more)`
          : body,
        title,
      );
    }

    return triggeredData;
  }, [alertSettings.rules, alertSettings.newOpportunitiesOnly, canTriggerAlert, evaluateRule, playAlertSound, sendAlertNotification]);

  // Toggle alert sound
  const toggleAlertSound = useCallback(() => {
    const newValue = !isAlertSoundEnabled;
    setIsAlertSoundEnabled(newValue);
    setAlertSettings(prev => ({ ...prev, soundEnabled: newValue }));
  }, [isAlertSoundEnabled]);

  // Toggle alert notifications
  const toggleAlertNotification = useCallback(() => {
    const newValue = !isAlertNotificationEnabled;
    setIsAlertNotificationEnabled(newValue);
    setAlertSettings(prev => ({ ...prev, notificationEnabled: newValue }));
  }, [isAlertNotificationEnabled]);

  // Handle alert settings change
  const handleAlertSettingsChange = useCallback((settings: AlertSettings) => {
    saveAlertSettings(settings);
  }, [saveAlertSettings]);

  // Clear all alerts
  const clearAllAlerts = useCallback(() => {
    setActiveAlerts([]);
  }, []);

  // Load alert settings (for compatibility)
  const loadAlertSettings = useCallback(() => {
    syncFromStore();
  }, [syncFromStore]);

  return {
    alertSettings,
    isAlertSoundEnabled,
    isAlertNotificationEnabled,
    playAlertSound,
    sendAlertNotification,
    handleAlertSettingsChange,
    toggleAlertSound,
    toggleAlertNotification,
    checkAndTriggerAlerts,
    clearAllAlerts,
    activeAlerts,
    loadAlertSettings,
    saveAlertSettings
  };
};
